import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, X, Camera, Trash2, User as UserIcon } from "lucide-react";
import api, { formatErr, fileUrl } from "../../lib/api";
import { BRAND_COLORS } from "../../lib/bookingStatus";

/**
 * Feb 2026 · Edit-Modal für den Kunden-Profil-Header (Admin-only).
 *
 * Öffnet sich über den „Bearbeiten"-Button im `CustomerProfileHeader`.
 * Postet `PATCH /team/customers/{id}/profile` mit den Feldern
 * `name`, `phone`, `birthday`, `bank_name`, `bank_iban`, `bank_bic`,
 * `bank_account_holder`. E-Mail bleibt read-only (an Auth gebunden).
 *
 * Profilfoto (Feb 2026 · Directive #292):
 * Klick auf das Foto oben im Modal öffnet einen Datei-Upload und tauscht
 * das Bild sofort aus. „Foto entfernen" setzt `avatar_file_id` auf null.
 * Uploads laufen über den bestehenden `POST /team/customers/{id}/avatar`
 * (admin-only), Kunde/Concierge bekommen 403.
 *
 * Nach Erfolg: `onSaved(customer)` liefert das aktualisierte User-Objekt,
 * damit der Parent-Screen die Karte ohne Full-Refetch aktualisieren kann.
 */
export function CustomerProfileEditModal({
  customerId,
  initial,
  onClose,
  onSaved,
}) {
  const nav = useNavigate();
  const [name, setName] = useState(initial?.name || "");
  const [phone, setPhone] = useState(initial?.phone || "");
  const [birthday, setBirthday] = useState(initial?.birthday || "");
  const [membershipTier, setMembershipTier] = useState(initial?.membership_tier || "");
  const [language, setLanguage] = useState(initial?.language || "de");
  const [bankName, setBankName] = useState(initial?.bank_name || "");
  const [bankHolder, setBankHolder] = useState(initial?.bank_account_holder || "");
  const [bankIban, setBankIban] = useState(initial?.bank_iban || "");
  const [bankBic, setBankBic] = useState(initial?.bank_bic || "");
  const [adminNote, setAdminNote] = useState(initial?.admin_note || "");
  // Feb 2026 · 17 Sep 2026 · sevDesk-Kundennummer (optional, Admin-only,
  // nachträglich pflegbar). Leerer String im Save-Body → Server-Unset.
  const [sevdeskCustNum, setSevdeskCustNum] = useState(initial?.sevdesk_customer_number || "");
  const [avatarFileId, setAvatarFileId] = useState(initial?.avatar_file_id || null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // ESC schließt den Dialog — Standard-UX-Erwartung.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onFilePicked = async (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Bitte eine Bilddatei wählen.");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Bild ist zu groß (max. 8 MB).");
      return;
    }
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(
        `/team/customers/${customerId}/avatar`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setAvatarFileId(data.avatar_file_id);
      // Header sofort aktualisieren, damit der Admin nach dem Speichern
      // nicht auf einen full-refresh warten muss.
      onSaved?.({ ...initial, avatar_file_id: data.avatar_file_id });
      toast.success("Profilfoto aktualisiert.");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setAvatarBusy(false);
    }
  };

  const removeAvatar = async () => {
    setAvatarBusy(true);
    try {
      await api.delete(`/team/customers/${customerId}/avatar`);
      setAvatarFileId(null);
      onSaved?.({ ...initial, avatar_file_id: null });
      toast.success("Profilfoto entfernt.");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setAvatarBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        phone: phone.trim(),
        birthday: birthday || "",
        membership_tier: membershipTier || "",
        language: language || "de",
        bank_name: bankName.trim(),
        bank_iban: bankIban.replace(/\s+/g, "").toUpperCase(),
        bank_bic: bankBic.replace(/\s+/g, "").toUpperCase(),
        bank_account_holder: bankHolder.trim(),
        admin_note: adminNote,
        sevdesk_customer_number: sevdeskCustNum.trim(),
      };
      const { data } = await api.patch(
        `/team/customers/${customerId}/profile`,
        body,
      );
      toast.success("Kundenprofil gespeichert.");
      // Feb 2026 · 27 Feb 2026 · User-Directive „Kundennummer-Auto-Vorschlag":
      // Falls der Kunde NACH dem Speichern immer noch keine Kundennummer
      // hat und im System unzugeordnete Rechnungen mit passender E-Mail
      // liegen, zeigen wir einen persistenten Hinweis-Toast mit Direkt-Link
      // in die sevDesk-Verwaltung. Kein Auto-Zuordnen — der Admin bestätigt.
      const hint = Number(data?.email_matched_unassigned_count || 0);
      if (hint > 0 && !body.sevdesk_customer_number) {
        toast(
          `${hint} nicht zugeordnete Rechnung${hint === 1 ? "" : "en"} mit dieser E-Mail vorhanden.`,
          {
            duration: 10000,
            description: "Jetzt zuordnen oder Kundennummer nachtragen?",
            action: {
              label: "Zuordnen",
              onClick: () => nav("/team/sevdesk"),
            },
            id: `customer-invoice-hint-${customerId}`,
            "data-testid": "customer-profile-invoice-hint-toast",
          },
        );
      }
      // Avatar-Änderung ist bereits separat committed — merge damit die
      // aktuelle Anzeige nicht überschrieben wird.
      onSaved?.({ ...data, avatar_file_id: avatarFileId });
      onClose?.();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-navy/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="customer-profile-edit-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-2xl bg-white shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-navy/10">
          <div>
            <div className="label-caps text-navy-dark/60">Kundenprofil</div>
            <h2 className="font-display text-xl md:text-2xl text-navy leading-tight">
              Stammdaten bearbeiten
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="customer-profile-edit-close"
            aria-label="Schließen"
            className="text-navy-dark/50 hover:text-navy p-1"
          >
            <X className="w-5 h-5" strokeWidth={1.8} />
          </button>
        </div>

        <div className="px-5 py-5 space-y-6">
          {/* Feb 2026 · Profilfoto — Klick auf das Bild öffnet den
              Datei-Upload. Live-Update ohne modal-close. */}
          <section>
            <div className="label-caps mb-3 text-navy-dark/60">Profilfoto</div>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={avatarBusy}
                data-testid="customer-profile-edit-avatar-btn"
                className="relative group w-20 h-20 rounded-full overflow-hidden border-2 border-navy/15 hover:border-gold focus:outline-none focus:border-gold transition-colors disabled:opacity-70 shrink-0"
                title="Klicken, um ein neues Foto hochzuladen"
                aria-label="Profilfoto ändern"
              >
                {avatarFileId ? (
                  <img
                    src={fileUrl(avatarFileId)}
                    alt="Profilfoto"
                    data-testid="customer-profile-edit-avatar-img"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{ background: BRAND_COLORS.navy }}
                    data-testid="customer-profile-edit-avatar-placeholder"
                  >
                    <UserIcon className="w-8 h-8 text-ivory/70" strokeWidth={1.4} />
                  </div>
                )}
                <span
                  className="absolute inset-0 flex items-center justify-center bg-navy/60 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-hidden="true"
                >
                  {avatarBusy ? (
                    <Loader2 className="w-5 h-5 text-ivory animate-spin" />
                  ) : (
                    <Camera className="w-5 h-5 text-ivory" strokeWidth={1.6} />
                  )}
                </span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onFilePicked}
                className="hidden"
                data-testid="customer-profile-edit-avatar-input"
              />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-navy-dark/80 leading-snug">
                  Klicken Sie auf das Foto, um ein neues Bild hochzuladen (max. 8 MB, JPG / PNG).
                </div>
                {avatarFileId && (
                  <button
                    type="button"
                    onClick={removeAvatar}
                    disabled={avatarBusy}
                    data-testid="customer-profile-edit-avatar-remove"
                    className="inline-flex items-center gap-1 mt-2 text-[11.5px] tracking-wider uppercase text-navy-dark/60 hover:text-red-700 disabled:opacity-50"
                  >
                    <Trash2 className="w-3 h-3" strokeWidth={1.8} />
                    Foto entfernen
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* Kontakt */}
          <section>
            <div className="label-caps mb-3 text-navy-dark/60">Kontakt</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Name" testid="customer-profile-edit-name">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="customer-profile-edit-name-input"
                  className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-3 py-2 text-[14px]"
                  placeholder="Vor- und Nachname"
                />
              </Field>
              <Field label="E-Mail (read-only)">
                <input
                  type="email"
                  value={initial?.email || ""}
                  readOnly
                  disabled
                  className="w-full bg-ivory/50 border border-navy/15 px-3 py-2 text-[14px] text-navy-dark/55 cursor-not-allowed"
                />
              </Field>
              <Field label="Telefon" testid="customer-profile-edit-phone">
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  data-testid="customer-profile-edit-phone-input"
                  className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-3 py-2 text-[14px]"
                  placeholder="+49 …"
                />
              </Field>
              <Field label="Geburtstag" testid="customer-profile-edit-birthday">
                <input
                  type="date"
                  value={birthday}
                  onChange={(e) => setBirthday(e.target.value)}
                  data-testid="customer-profile-edit-birthday-input"
                  className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-3 py-2 text-[14px]"
                />
              </Field>
              <Field label="Sprache" testid="customer-profile-edit-language">
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  data-testid="customer-profile-edit-language-input"
                  className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-3 py-2 text-[14px]"
                >
                  <option value="de">Deutsch</option>
                  <option value="en">Englisch</option>
                </select>
              </Field>
            </div>
          </section>

          {/* Bank */}
          <section>
            <div className="label-caps mb-3 text-navy-dark/60">Bankverbindung</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Kontoinhaber" testid="customer-profile-edit-holder">
                <input
                  type="text"
                  value={bankHolder}
                  onChange={(e) => setBankHolder(e.target.value)}
                  data-testid="customer-profile-edit-holder-input"
                  className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-3 py-2 text-[14px]"
                />
              </Field>
              <Field label="Bank" testid="customer-profile-edit-bankname">
                <input
                  type="text"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  data-testid="customer-profile-edit-bankname-input"
                  className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-3 py-2 text-[14px]"
                  placeholder="z. B. Deutsche Kreditbank"
                />
              </Field>
              <Field label="IBAN" testid="customer-profile-edit-iban">
                <input
                  type="text"
                  value={bankIban}
                  onChange={(e) => setBankIban(e.target.value)}
                  data-testid="customer-profile-edit-iban-input"
                  className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-3 py-2 text-[14px] font-mono tracking-wider uppercase"
                  placeholder="DE00 0000 0000 0000 0000 00"
                  autoComplete="off"
                />
              </Field>
              <Field label="BIC" testid="customer-profile-edit-bic">
                <input
                  type="text"
                  value={bankBic}
                  onChange={(e) => setBankBic(e.target.value)}
                  data-testid="customer-profile-edit-bic-input"
                  className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-3 py-2 text-[14px] font-mono tracking-wider uppercase"
                  placeholder="XXXXDEXXXXX"
                  autoComplete="off"
                />
              </Field>
            </div>
            <p className="mt-2 text-[11.5px] text-navy-dark/50">
              Bankdaten sind nur für Admin und den zugewiesenen Concierge sichtbar.
              IBAN wird im Header standardmäßig maskiert.
            </p>
          </section>

          {/* Feb 2026 · Directive #298: Interne Anmerkung — nur Admin-Bereich.
              Wird via `/auth/me`-Strip niemals an den Kunden ausgeliefert. */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="label-caps text-navy-dark/60">Interne Anmerkung</div>
              <span
                className="inline-flex items-center gap-1 text-[10.5px] tracking-widest uppercase text-navy-dark/45"
                title="Für den Kunden nicht sichtbar"
              >
                Admin-only
              </span>
            </div>
            <textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              data-testid="customer-profile-edit-admin-note-input"
              rows={4}
              maxLength={4000}
              placeholder="Freie Notiz zum Kunden — z. B. besondere Wünsche, Zugangsdetails, Kommunikationsstil."
              className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-3 py-2 text-[13.5px] leading-snug resize-y"
            />
            <p className="mt-1.5 text-[11.5px] text-navy-dark/50">
              Diese Notiz ist nur im Admin-Bereich sichtbar. Der Kunde bekommt sie in keiner Ansicht angezeigt.
            </p>
          </section>

          {/* Feb 2026 · 17 Sep 2026 · sevDesk-Kundennummer. Optional,
              nachträglich pflegbar. Wird sie eingetragen oder geändert,
              ordnet der Server bisher unzugeordnete sevDesk-Rechnungen
              automatisch diesem Kunden zu. */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="label-caps text-navy-dark/60">Rechnungen</div>
              <span
                className="inline-flex items-center gap-1 text-[10.5px] tracking-widest uppercase text-navy-dark/45"
                title="Nur für Admins editierbar"
              >
                Optional
              </span>
            </div>
            <Field label="Kundennummer" testid="customer-profile-edit-sevdesk">
              <input
                type="text"
                value={sevdeskCustNum}
                onChange={(e) => setSevdeskCustNum(e.target.value)}
                data-testid="customer-profile-edit-sevdesk-input"
                className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-3 py-2 text-[14px] font-mono tracking-wider"
                placeholder="z. B. 1004"
                autoComplete="off"
              />
            </Field>
            <p className="mt-1.5 text-[11.5px] text-navy-dark/50">
              Optional. Dient dazu, hochgeladene Rechnungs-PDFs automatisch dem Kunden zuzuordnen, wenn der Dateiname die Nummer enthält.
            </p>
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-navy/10 bg-ivory/40">
          <button
            type="button"
            onClick={onClose}
            data-testid="customer-profile-edit-cancel"
            className="px-4 py-2 text-[12px] tracking-wider uppercase text-navy-dark/70 hover:text-navy"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="customer-profile-edit-save"
            className="inline-flex items-center gap-1.5 bg-navy text-ivory hover:bg-navy-dark disabled:opacity-60 px-4 py-2 text-[12px] tracking-wider uppercase"
            style={{ background: BRAND_COLORS.navy }}
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block text-[11px] tracking-widest uppercase text-navy-dark/60 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
